import cx from 'classnames';
import type {
  FunctionComponent,
  ReactElement,
  MouseEvent as ReactMouseEvent,
  Ref,
} from 'react';
import { useCallback } from 'react';

import { ComponentWrapper } from '@trello/component-wrapper';
import { DynamicButton } from '@trello/dynamic-tokens';
import { MemberAvatar } from '@trello/member-avatar';

import { PopOver } from 'app/scripts/views/lib/PopOver';
import { LazyProfileCard } from 'app/src/components/ProfileCard';
import { DraggableAvatar } from './DraggableAvatar';

import styles from './Facepile.less';

interface FacepileProps {
  className?: string;
  avatarSize?: number;
  maxFaceCount?: number;
  showMore?: boolean;
  memberIds: string[];
  renderAvatar?: (memberId: string) => ReactElement;
  onShowMoreClick?: () => void;
  showMoreRef?: Ref<HTMLButtonElement>;
  showMoreTestId?: string;
  showProfileCards?: boolean;
  boardId?: string;
  idOrganization?: string;
  renderingInMenu?: boolean;
}

export const Facepile: FunctionComponent<FacepileProps> = ({
  className = '',
  avatarSize = 30,
  maxFaceCount = 5,
  showMore = true,
  memberIds,
  renderAvatar,
  onShowMoreClick,
  showMoreRef,
  showMoreTestId,
  showProfileCards = true,
  idOrganization,
  renderingInMenu,
}) => {
  const onCloseProfileCard = useCallback(() => PopOver.hide(), []);

  const onOpenProfileCard = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (idMember: any) => {
      return (e: ReactMouseEvent) =>
        PopOver.toggle({
          elem: e.currentTarget,
          hideHeader: true,
          reactElement: (
            <ComponentWrapper>
              <LazyProfileCard
                onClose={onCloseProfileCard}
                memberId={idMember}
              />
            </ComponentWrapper>
          ),
        });
    },
    [onCloseProfileCard],
  );

  const avatars = memberIds.slice(0, maxFaceCount).map((idMember) => {
    if (renderAvatar) {
      return renderAvatar(idMember);
    }
    return (
      <MemberAvatar
        key={idMember}
        idMember={idMember}
        size={avatarSize}
        {...(showProfileCards && {
          onClick: onOpenProfileCard(idMember),
        })}
        testId="facepile-member-avatar"
      />
    );
  });

  const shouldRenderShowMore = !!showMore && memberIds.length > maxFaceCount;

  const showMoreButton = onShowMoreClick ? (
    <div>
      <DynamicButton
        className={cx({
          [styles.showMore]: true,
          [styles.showMoreClickable]: true,
          [styles.boardMenuShowMoreClickable]: renderingInMenu,
        })}
        ref={showMoreRef}
        onClick={onShowMoreClick}
        style={{ width: avatarSize, height: avatarSize }}
        data-testid={showMoreTestId}
      >
        +{memberIds.length - maxFaceCount}
      </DynamicButton>
    </div>
  ) : (
    <div
      className={cx(styles.showMore, styles.showMoreDisabled)}
      style={{ width: avatarSize, height: avatarSize }}
    >
      +{memberIds.length - maxFaceCount}
    </div>
  );

  return (
    <div
      className={cx(styles.facepile, className)}
      ref={
        !shouldRenderShowMore && renderingInMenu
          ? (showMoreRef as Ref<HTMLDivElement>)
          : null
      }
    >
      {avatars.map((avatar, index) => (
        <DraggableAvatar
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={index}
          index={index}
          memberId={memberIds[index]}
          organizationId={idOrganization}
          avatarSize={avatarSize}
          avatarCount={avatars.length}
        >
          {avatar}
        </DraggableAvatar>
      ))}
      {shouldRenderShowMore && <>{showMoreButton}</>}
    </div>
  );
};
